import { useEffect, useRef, useState } from "react";
import { SimpleMarkdown } from "../../../shared/components/SimpleMarkdown.tsx";
import {
  buildCallPrepClipboardText,
  CallPrepStatusBadge,
  CallPrepStructuredView,
} from "../../ev-workstation/components/CallPrepStructuredView.tsx";
import { EvHandoffStatusCard } from "../../ev-workstation/components/EvHandoffStatusCard.tsx";
import {
  useCreateEvWorkflowEventMutation,
  useEvAgentContextQuery,
  useEvOwnerOptionsQuery,
  useEvProjectCallPrepStatusQuery,
  useEvProjectCallPrepStatusStream,
  usePrepareEvCallReadyMutation,
  useQueueEvHandoffMutation,
  useRegenerateEvCallPrepMutation,
  useRetryEvHandoffMutation,
  useUpdateEvWorkflowMutation,
} from "../../ev-workstation/hooks/use-ev-workstation-queries.ts";
import {
  isActiveCallPrepRuntimeStatus,
  toCallPrepVisualStatus,
} from "../../ev-workstation/lib/call-prep-status.ts";
import type {
  EvOperatingSummaryArtifact,
  EvWorkflowPatch,
  EvWorkstationDetail,
} from "../../ev-workstation/types/index.ts";
import {
  formatWorkflowTimelineEventLabel,
  isVisibleWorkflowTimelineEvent,
} from "../lib/workflow-timeline.ts";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to the legacy copy path below.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  try {
    const copied = document.execCommand("copy");
    if (!copied) {
      throw new Error("document.execCommand(copy) returned false");
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

type ProjectWorkflowTab =
  | "workflow"
  | "relationship_history"
  | "next_step"
  | "call_prep"
  | "handoff";

const PROJECT_WORKFLOW_TABS: Array<{ id: ProjectWorkflowTab; label: string }> = [
  { id: "workflow", label: "Workflow" },
  { id: "relationship_history", label: "Relationship History" },
  { id: "next_step", label: "Next Step" },
  { id: "call_prep", label: "Call Prep" },
  { id: "handoff", label: "Handoff" },
];

interface ProjectCallPrepSectionProps {
  detail: EvWorkstationDetail;
  onRefresh: () => void | Promise<void>;
}

export function ProjectCallPrepSection({ detail, onRefresh }: ProjectCallPrepSectionProps) {
  const callPrepStatusQuery = useEvProjectCallPrepStatusQuery(detail.project.id, true);
  const prepareCallReadyMutation = usePrepareEvCallReadyMutation();
  const regenerateCallPrepMutation = useRegenerateEvCallPrepMutation();
  const [showSources, setShowSources] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const lastStatusRef = useRef<string | null>(null);
  const callPrepArtifact = detail.artifacts.callPrep;
  const liveCallPrepStatus = callPrepStatusQuery.data?.status ?? callPrepArtifact?.status ?? null;
  const isPreparedForCall = detail.workflow.callReadyStatus !== "idle";
  const callPrepVisualStatus = toCallPrepVisualStatus({
    callReadyStatus: detail.workflow.callReadyStatus,
    artifactStatus: callPrepArtifact?.status,
    artifactPayload: callPrepArtifact?.payload,
    artifactFreshnessState: callPrepArtifact?.freshnessState ?? null,
    liveStatus: liveCallPrepStatus,
    liveFreshnessState:
      callPrepStatusQuery.data?.freshnessState ?? callPrepArtifact?.freshnessState,
  });
  const isStreamingCallPrepStatus = isActiveCallPrepRuntimeStatus(liveCallPrepStatus);
  const canPrepareCall = detail.workflow.callReadyStatus === "idle";
  const canRegenerateCallPrep = isPreparedForCall;
  const prepareCallLabel = prepareCallReadyMutation.isPending ? "Preparing..." : "Prepare Call";
  const regenerateCallLabel = !canRegenerateCallPrep
    ? "Prepare call first"
    : regenerateCallPrepMutation.isPending
      ? "Regenerating..."
      : "Regenerate call prep";
  const hasExistingCallPrep = Boolean(callPrepArtifact?.payload);
  const lastGeneratedAt = callPrepArtifact?.lastGeneratedAt ?? detail.workflow.callReadyCompletedAt;
  const callReadyMeta =
    callPrepVisualStatus === "idle"
      ? "No call prep has been requested yet."
      : callPrepVisualStatus === "needs_input"
        ? "There is not enough relationship or commercial information yet to generate a credible call brief."
        : callPrepVisualStatus === "stale"
          ? hasExistingCallPrep
            ? "The current brief is still available, but it needs an update because the workflow changed."
            : "Call prep needs an update before the next call."
          : callPrepVisualStatus === "failed"
            ? hasExistingCallPrep
              ? "Updating the existing call prep failed. Review the last available brief and regenerate."
              : "Call prep generation failed. Review the project context and regenerate."
            : callPrepVisualStatus === "fresh"
              ? lastGeneratedAt
                ? `Call prep brief generated ${formatDateTime(lastGeneratedAt)}.`
                : "Call prep brief is available."
              : hasExistingCallPrep
                ? "Updating the existing call prep with the latest workflow context."
                : detail.workflow.callReadyRequestedAt
                  ? `Queued ${formatDateTime(detail.workflow.callReadyRequestedAt)} for AI preparation.`
                  : "Queued for AI preparation.";

  useEvProjectCallPrepStatusStream(detail.project.id, isStreamingCallPrepStatus);

  useEffect(() => {
    const previous = lastStatusRef.current;
    const current = liveCallPrepStatus;
    const wasActive =
      previous === "queued" ||
      previous === "retrieving" ||
      previous === "drafting" ||
      previous === "validating";
    const isTerminal = current === "completed" || current === "needs_input" || current === "failed";

    if (wasActive && isTerminal) {
      void onRefresh();
    }

    lastStatusRef.current = current;
  }, [liveCallPrepStatus, onRefresh]);

  return (
    <div className="project-workflow-panel__call-prep">
      <div className="project-workflow-panel__call-prep-head">
        <div>
          <div className="project-workflow-panel__artifact-title">Call Prep</div>
          <div className="project-workflow-panel__artifact-meta">{callReadyMeta}</div>
          <div className="project-workflow-panel__artifact-meta">
            {callPrepArtifact?.generatedByModel
              ? `Generated by ${callPrepArtifact.generatedByModel}`
              : "No generated call prep artifact yet."}
          </div>
          {callPrepStatusQuery.data?.progressMessage ? (
            <div className="project-workflow-panel__artifact-meta project-workflow-panel__artifact-meta--live">
              {callPrepStatusQuery.data.progressMessage}
            </div>
          ) : null}
          {callPrepStatusQuery.data?.errorMessage ? (
            <div className="ev-handoff-card__error">{callPrepStatusQuery.data.errorMessage}</div>
          ) : null}
        </div>
        <div className="project-workflow-panel__call-prep-actions">
          {isPreparedForCall ? (
            <CallPrepStatusBadge status={callPrepVisualStatus} />
          ) : (
            <span className="project-workflow-panel__call-ready-idle">Not prepared</span>
          )}
          <button
            type="button"
            className="btn btn-small btn-secondary"
            disabled={!canPrepareCall || prepareCallReadyMutation.isPending}
            onClick={() =>
              prepareCallReadyMutation.mutate(
                {
                  projectId: detail.project.id,
                  request: {
                    reason: "operator_request",
                    confirmedByHuman: true,
                    source: "manual",
                    scheduledAt: detail.workflow.nextTouchpointAt,
                    calendarEventId: detail.workflow.nextTouchpointCalendarEventId,
                  },
                },
                {
                  onSuccess: () => {
                    void onRefresh();
                  },
                }
              )
            }
          >
            {canPrepareCall ? prepareCallLabel : "Prepared"}
          </button>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            disabled={!canRegenerateCallPrep || regenerateCallPrepMutation.isPending}
            onClick={() =>
              regenerateCallPrepMutation.mutate(detail.project.id, {
                onSuccess: () => {
                  void onRefresh();
                },
              })
            }
          >
            {regenerateCallLabel}
          </button>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            disabled={!callPrepArtifact?.payload}
            onClick={() => {
              if (!callPrepArtifact?.payload) {
                setCopyState("failed");
                return;
              }

              void copyTextToClipboard(
                buildCallPrepClipboardText(
                  detail.project.name ?? `@${detail.project.twitter_handle}`,
                  callPrepVisualStatus,
                  {
                    summary: callPrepArtifact.payload.summary,
                    objective: callPrepArtifact.payload.objective,
                    whyNow: callPrepArtifact.payload.whyNow,
                    relationshipSnapshot: callPrepArtifact.payload.relationshipSnapshot,
                    priorCallHighlights: callPrepArtifact.payload.priorCallHighlights,
                    commercialState: callPrepArtifact.payload.commercialState,
                    teamInvestorContext: callPrepArtifact.payload.teamInvestorContext,
                    talkingPoints: callPrepArtifact.payload.talkingPoints,
                    questionsToAsk: callPrepArtifact.payload.questionsToAsk,
                    risksObjections: callPrepArtifact.payload.risksObjections,
                    missingInputs: callPrepArtifact.payload.missingInputs,
                    desiredNextStep: callPrepArtifact.payload.desiredNextStep,
                  }
                )
              )
                .then(() => {
                  setCopyState("copied");
                  setTimeout(() => setCopyState("idle"), 1500);
                })
                .catch(() => {
                  setCopyState("failed");
                  setTimeout(() => setCopyState("idle"), 1500);
                });
            }}
          >
            {copyState === "copied"
              ? "Copied"
              : copyState === "failed"
                ? "Copy failed"
                : "Copy call brief"}
          </button>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={() => setShowSources((current) => !current)}
          >
            {showSources ? "Hide sources" : "View sources"}
          </button>
        </div>
      </div>

      {callPrepArtifact?.payload ? (
        <CallPrepStructuredView
          status={callPrepVisualStatus}
          showProvenance={!showSources}
          data={{
            summary: callPrepArtifact.payload.summary,
            objective: callPrepArtifact.payload.objective,
            whyNow: callPrepArtifact.payload.whyNow,
            relationshipSnapshot: callPrepArtifact.payload.relationshipSnapshot,
            priorCallHighlights: callPrepArtifact.payload.priorCallHighlights,
            commercialState: callPrepArtifact.payload.commercialState,
            teamInvestorContext: callPrepArtifact.payload.teamInvestorContext,
            talkingPoints: callPrepArtifact.payload.talkingPoints,
            questionsToAsk: callPrepArtifact.payload.questionsToAsk,
            risksObjections: callPrepArtifact.payload.risksObjections,
            missingInputs: callPrepArtifact.payload.missingInputs,
            desiredNextStep: callPrepArtifact.payload.desiredNextStep,
            sourceSummary: callPrepArtifact.sourceSummary,
            qualityFlags: callPrepArtifact.qualityFlags,
            openQuestions: callPrepArtifact.openQuestions,
            generatedByModel:
              callPrepStatusQuery.data?.generatedByModel ?? callPrepArtifact.generatedByModel,
            generatedAt: callPrepStatusQuery.data?.generatedAt ?? callPrepArtifact.lastGeneratedAt,
            freshnessState:
              callPrepStatusQuery.data?.freshnessState ?? callPrepArtifact.freshnessState,
          }}
        />
      ) : (
        <div className="community-empty">
          {isActiveCallPrepRuntimeStatus(liveCallPrepStatus)
            ? "Generating call prep..."
            : !isPreparedForCall
              ? "Prepare this project for calls to start the AI call-prep workflow."
              : "Call prep is unavailable for this project."}
        </div>
      )}

      {showSources ? (
        <div className="project-workflow-panel__call-prep-sources">
          <div className="project-workflow-panel__artifact-title">Generated From</div>
          <ul className="call-prep-structured__list">
            {(callPrepArtifact?.sourceSummary ?? []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {callPrepArtifact?.qualityFlags?.length ? (
            <>
              <div className="project-workflow-panel__artifact-title">Quality Flags</div>
              <ul className="call-prep-structured__list">
                {callPrepArtifact.qualityFlags.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          ) : null}
          {callPrepArtifact?.openQuestions?.length ? (
            <>
              <div className="project-workflow-panel__artifact-title">Open Questions</div>
              <ul className="call-prep-structured__list">
                {callPrepArtifact.openQuestions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface ProjectWorkflowPanelProps {
  detail: EvWorkstationDetail;
  onRefresh: () => void | Promise<void>;
}

function buildOperatingSummaryMarkdown(
  payload: EvOperatingSummaryArtifact | null | undefined
): string {
  if (!payload) {
    return "No generated summary yet.";
  }

  return [
    payload.summary,
    payload.whyItMatters ? `### Why It Matters\n${payload.whyItMatters}` : null,
    payload.relationshipStatus ? `### Relationship Status\n${payload.relationshipStatus}` : null,
    payload.immediateNextAction
      ? `### Immediate Next Action\n${payload.immediateNextAction}`
      : null,
  ]
    .filter((section): section is string => Boolean(section))
    .join("\n\n");
}

function formatCommunicationCounterparty(name: string | null, email: string | null): string {
  if (name && email) {
    return `${name} <${email}>`;
  }

  return name ?? email ?? "Unknown participant";
}

export function ProjectOperatingSummaryArtifact({ detail }: { detail: EvWorkstationDetail }) {
  const operatingSummary = detail.artifacts.operatingSummary?.payload;

  return (
    <div className="project-workflow-panel__artifact">
      <div className="project-workflow-panel__artifact-title">Operating Summary</div>
      <SimpleMarkdown
        markdown={buildOperatingSummaryMarkdown(operatingSummary)}
        className="markdown-block markdown-block--workflow"
      />
      {operatingSummary?.keyPoints?.length ? (
        <SimpleMarkdown
          markdown={`### Key points\n${operatingSummary.keyPoints.map((point) => `- ${point}`).join("\n")}`}
          className="markdown-block markdown-block--workflow markdown-block--meta"
        />
      ) : null}
      {operatingSummary?.keyBlockers?.length ? (
        <SimpleMarkdown
          markdown={`### Blockers\n${operatingSummary.keyBlockers.map((point) => `- ${point}`).join("\n")}`}
          className="markdown-block markdown-block--workflow markdown-block--meta"
        />
      ) : null}
    </div>
  );
}

export function ProjectWorkflowPanel({ detail, onRefresh }: ProjectWorkflowPanelProps) {
  const ownersQuery = useEvOwnerOptionsQuery(true);
  const updateMutation = useUpdateEvWorkflowMutation();
  const createEventMutation = useCreateEvWorkflowEventMutation();
  const queueHandoffMutation = useQueueEvHandoffMutation();
  const retryHandoffMutation = useRetryEvHandoffMutation();
  const [activeTab, setActiveTab] = useState<ProjectWorkflowTab>("workflow");
  const [eventNote, setEventNote] = useState("");
  const loadAgentContext = activeTab === "relationship_history" || activeTab === "call_prep";
  const agentContextQuery = useEvAgentContextQuery(detail.project.id, loadAgentContext);

  const workflow = detail.workflow;
  const artifacts = detail.artifacts;
  const latestHandoff = detail.handoffJobs[0] ?? detail.latestHandoff;
  const visibleEvents = detail.events.filter(isVisibleWorkflowTimelineEvent);
  const ownerValue = workflow.ownerEmail ?? "unassigned";
  const owners = ownersQuery.data ?? [];
  const relationshipSnapshot = [
    workflow.referredBy
      ? `Referral path: ${workflow.referredBy}`
      : "Referral path is not recorded.",
    workflow.groupChatUrl ? "Group chat is attached." : "Group chat is missing.",
    workflow.transcriptUrl ? "Transcript link is attached." : "Transcript link is missing.",
    workflow.ownerEmail ? `Owner: ${workflow.ownerEmail}` : "Owner is unassigned.",
    workflow.projectFollowUpDate
      ? `Project follow-up date: ${workflow.projectFollowUpDate}`
      : "Project follow-up date is not set.",
    workflow.warmIntroFollowUpDate
      ? `Warm/intro follow-up date: ${workflow.warmIntroFollowUpDate}`
      : "Warm/intro follow-up date is not set.",
    workflow.nextTouchpointType !== "unclear"
      ? `Next touchpoint: ${workflow.nextTouchpointType} via ${workflow.nextTouchpointChannel}.`
      : "Next touchpoint type is not clarified.",
  ];
  const handoffSummary = artifacts.handoffSummary?.payload;
  const communicationContext = agentContextQuery.data?.communications;

  function updateWorkflow(patch: EvWorkflowPatch) {
    updateMutation.mutate(
      { projectId: detail.project.id, patch },
      {
        onSuccess: () => {
          void onRefresh();
        },
      }
    );
  }

  return (
    <div
      className="project-workflow-panel"
      key={workflow.updatedAt ?? workflow.createdAt ?? workflow.projectId}
    >
      <div className="project-workflow-panel__header">
        <span className="section-title">BD Workflow</span>
        <div className="project-workflow-panel__flags">
          {detail.flags.overdue && <span className="trend-down">Overdue</span>}
          {detail.flags.dueToday && <span className="trend-up">Due Today</span>}
          {detail.flags.warmIntroFollowUpOverdue && (
            <span className="trend-down">Warm Intro Overdue</span>
          )}
          {detail.flags.unassigned && <span className="confidence-tier-label">Unassigned</span>}
        </div>
      </div>

      <div
        className="project-workflow-panel__tabs"
        role="tablist"
        aria-label="Project workflow sections"
      >
        {PROJECT_WORKFLOW_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`project-workflow-panel__tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <section hidden={activeTab !== "workflow"} className="project-workflow-panel__tab-section">
        <div className="project-workflow-panel__action-plan">
          <div className="project-workflow-panel__artifact-title">Action Plan</div>
          <div className="project-workflow-panel__artifact-meta">
            Keep the next operator step here. Workflow notes and change history live in the
            relationship timeline composer.
          </div>
          <div className="project-workflow-panel__grid project-workflow-panel__grid--stacked">
            <label className="project-workflow-panel__field project-workflow-panel__field--full">
              <span>Next Action</span>
              <textarea
                className="community-input"
                rows={3}
                defaultValue={workflow.nextAction ?? ""}
                placeholder="What needs to happen next?"
                onBlur={(e) => updateWorkflow({ nextAction: e.target.value || null })}
              />
            </label>

            <label className="project-workflow-panel__field project-workflow-panel__field--full">
              <span>Operator Context</span>
              <textarea
                className="community-input"
                rows={4}
                defaultValue={workflow.internalNotes ?? ""}
                placeholder="Private context, blockers, or reminders that should stay on the workflow record."
                onBlur={(e) => updateWorkflow({ internalNotes: e.target.value || null })}
              />
            </label>
          </div>
        </div>

        <div className="project-workflow-panel__grid">
          <label className="project-workflow-panel__field">
            <span>Stage</span>
            <select
              className="community-input"
              value={workflow.stage}
              onChange={(e) =>
                updateWorkflow({ stage: e.target.value as EvWorkflowPatch["stage"] })
              }
            >
              <option value="lead">lead</option>
              <option value="group_created">group_created</option>
              <option value="call_completed">call_completed</option>
              <option value="passed_ic">passed_ic</option>
              <option value="qualified_for_sale">qualified_for_sale</option>
              <option value="contract_shared">contract_shared</option>
              <option value="closed_onboarding">closed_onboarding</option>
              <option value="ngmi">ngmi</option>
              <option value="no_token">no_token</option>
              <option value="no_for_now">no_for_now</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Recommended Track</span>
            <select
              className="community-input"
              value={workflow.initialSaleHypothesis}
              onChange={(e) =>
                updateWorkflow({
                  initialSaleHypothesis: e.target.value as EvWorkflowPatch["initialSaleHypothesis"],
                })
              }
            >
              <option value="unclear">unclear</option>
              <option value="prime">prime</option>
              <option value="legion_only">legion_only</option>
              <option value="private">private</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Confirmed Track</span>
            <select
              className="community-input"
              value={workflow.confirmedSaleType}
              onChange={(e) =>
                updateWorkflow({
                  confirmedSaleType: e.target.value as EvWorkflowPatch["confirmedSaleType"],
                })
              }
            >
              <option value="none">none</option>
              <option value="prime">prime</option>
              <option value="legion_only">legion_only</option>
              <option value="private">private</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Priority</span>
            <select
              className="community-input"
              value={workflow.priority}
              onChange={(e) =>
                updateWorkflow({ priority: e.target.value as EvWorkflowPatch["priority"] })
              }
            >
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Owner</span>
            <select
              className="community-input"
              value={ownerValue}
              onChange={(e) => {
                if (e.target.value === "unassigned") {
                  updateWorkflow({ ownerEmail: null, ownerUid: null });
                  return;
                }

                const selectedOwner = owners.find((owner) => owner.email === e.target.value);
                updateWorkflow({
                  ownerEmail: selectedOwner?.email ?? null,
                  ownerUid: selectedOwner?.uid ?? null,
                });
              }}
            >
              <option value="unassigned">unassigned</option>
              {owners
                .filter((owner) => owner.email)
                .map((owner) => (
                  <option key={owner.uid} value={owner.email ?? ""}>
                    {owner.email}
                  </option>
                ))}
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Project Follow-Up</span>
            <input
              type="date"
              className="community-input"
              defaultValue={workflow.projectFollowUpDate ?? workflow.followUpDate ?? ""}
              onBlur={(e) => updateWorkflow({ projectFollowUpDate: e.target.value || null })}
            />
          </label>

          <label className="project-workflow-panel__field">
            <span>Warm/Intro Follow-Up</span>
            <input
              type="date"
              className="community-input"
              defaultValue={workflow.warmIntroFollowUpDate ?? ""}
              onBlur={(e) => updateWorkflow({ warmIntroFollowUpDate: e.target.value || null })}
            />
            <small className="project-workflow-panel__hint">
              {workflow.warmIntroFollowUpReason ??
                "Set when intro routing needs a separate follow-up lane."}
            </small>
          </label>

          <label className="project-workflow-panel__field">
            <span>Next Touchpoint Type</span>
            <select
              className="community-input"
              value={workflow.nextTouchpointType}
              onChange={(e) =>
                updateWorkflow({
                  nextTouchpointType: e.target.value as EvWorkflowPatch["nextTouchpointType"],
                })
              }
            >
              <option value="unclear">unclear</option>
              <option value="call">call</option>
              <option value="async_follow_up">async_follow_up</option>
              <option value="handoff">handoff</option>
              <option value="internal">internal</option>
              <option value="other">other</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Touchpoint Channel</span>
            <select
              className="community-input"
              value={workflow.nextTouchpointChannel}
              onChange={(e) =>
                updateWorkflow({
                  nextTouchpointChannel: e.target.value as EvWorkflowPatch["nextTouchpointChannel"],
                })
              }
            >
              <option value="unclear">unclear</option>
              <option value="telegram">telegram</option>
              <option value="email">email</option>
              <option value="discord">discord</option>
              <option value="slack">slack</option>
              <option value="group_chat">group_chat</option>
              <option value="phone">phone</option>
              <option value="zoom">zoom</option>
              <option value="google_meet">google_meet</option>
              <option value="other">other</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Touchpoint Time</span>
            <input
              type="datetime-local"
              className="community-input"
              defaultValue={
                workflow.nextTouchpointAt
                  ? new Date(workflow.nextTouchpointAt).toISOString().slice(0, 16)
                  : ""
              }
              onBlur={(e) =>
                updateWorkflow({
                  nextTouchpointAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                })
              }
            />
          </label>

          <label className="project-workflow-panel__field">
            <span>Human Confirmed Call</span>
            <select
              className="community-input"
              value={workflow.nextTouchpointHumanConfirmed ? "yes" : "no"}
              onChange={(e) =>
                updateWorkflow({
                  nextTouchpointHumanConfirmed: e.target.value === "yes",
                })
              }
            >
              <option value="no">no</option>
              <option value="yes">yes</option>
            </select>
          </label>

          <label className="project-workflow-panel__field">
            <span>Transcript URL</span>
            <input
              type="url"
              className="community-input"
              defaultValue={workflow.transcriptUrl ?? ""}
              onBlur={(e) => updateWorkflow({ transcriptUrl: e.target.value || null })}
            />
          </label>

          <label className="project-workflow-panel__field">
            <span>Group Chat URL / Handle / ID</span>
            <input
              type="text"
              className="community-input"
              defaultValue={workflow.groupChatUrl ?? ""}
              onBlur={(e) => updateWorkflow({ groupChatUrl: e.target.value || null })}
              placeholder="https://t.me/..., @handle, or chat ID"
            />
          </label>

          <label className="project-workflow-panel__field">
            <span>Referred By</span>
            <input
              type="text"
              className="community-input"
              defaultValue={workflow.referredBy ?? ""}
              onBlur={(e) => updateWorkflow({ referredBy: e.target.value || null })}
            />
          </label>

          <label className="project-workflow-panel__field">
            <span>Source Detail</span>
            <input
              type="text"
              className="community-input"
              defaultValue={workflow.sourceDetail ?? ""}
              onBlur={(e) => updateWorkflow({ sourceDetail: e.target.value || null })}
            />
          </label>
        </div>

        <div className="project-workflow-panel__grid project-workflow-panel__grid--stacked">
          <label className="project-workflow-panel__field project-workflow-panel__field--full">
            <span>Call Summary</span>
            <textarea
              className="community-input"
              rows={3}
              defaultValue={workflow.callSummary ?? ""}
              onBlur={(e) => updateWorkflow({ callSummary: e.target.value || null })}
            />
          </label>
        </div>

        <div className="project-workflow-panel__meta">
          <span>Last contact: {formatDateTime(workflow.lastContactAt)}</span>
          <span>Touchpoint at: {formatDateTime(workflow.nextTouchpointAt)}</span>
          <span>Passed IC: {formatDateTime(workflow.passedIcAt)}</span>
          <span>Contract shared: {formatDateTime(workflow.contractSharedAt)}</span>
        </div>
      </section>

      <section hidden={activeTab !== "handoff"} className="project-workflow-panel__tab-section">
        <EvHandoffStatusCard
          workflow={workflow}
          handoff={latestHandoff}
          queueing={queueHandoffMutation.isPending}
          retrying={retryHandoffMutation.isPending}
          onQueueHandoff={() =>
            queueHandoffMutation.mutate(detail.project.id, {
              onSuccess: () => {
                void onRefresh();
              },
            })
          }
          onRetryHandoff={() => {
            if (!latestHandoff) return;
            retryHandoffMutation.mutate(
              {
                handoffId: latestHandoff.id,
                projectId: detail.project.id,
              },
              {
                onSuccess: () => {
                  void onRefresh();
                },
              }
            );
          }}
        />
        <div className="project-workflow-panel__artifacts project-workflow-panel__artifacts--single">
          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Generated Handoff Summary</div>
            <SimpleMarkdown
              markdown={
                handoffSummary
                  ? [
                      `### Readiness\n${handoffSummary.ready ? "Ready for onboarding handoff." : "Not ready for onboarding handoff yet."}`,
                      `### Template\n${handoffSummary.template}`,
                      `### Confidence\n${handoffSummary.confidence}`,
                      (handoffSummary.checklist ?? []).length
                        ? `### Checklist\n${(handoffSummary.checklist ?? []).map((item) => `- ${item}`).join("\n")}`
                        : null,
                      (handoffSummary.blockers ?? []).length
                        ? `### Blockers\n${(handoffSummary.blockers ?? []).map((item) => `- ${item}`).join("\n")}`
                        : null,
                      handoffSummary.ownerFollowThrough
                        ? `### Owner Follow-Through\n${handoffSummary.ownerFollowThrough}`
                        : null,
                      handoffSummary.latestError
                        ? `### Latest Error\n${handoffSummary.latestError}`
                        : null,
                    ]
                      .filter((section): section is string => Boolean(section))
                      .join("\n\n")
                  : "No generated handoff summary yet."
              }
              className="markdown-block markdown-block--workflow"
            />
          </div>
        </div>
      </section>

      <section hidden={activeTab !== "next_step"} className="project-workflow-panel__tab-section">
        <div className="project-workflow-panel__artifacts">
          <ProjectOperatingSummaryArtifact detail={detail} />

          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Next Step</div>
            <SimpleMarkdown
              markdown={
                artifacts.nextStepRecommendation?.payload?.action ??
                workflow.nextAction ??
                "No recommendation yet."
              }
              className="markdown-block markdown-block--workflow"
            />
            {artifacts.nextStepRecommendation?.payload?.reason ? (
              <SimpleMarkdown
                markdown={[
                  `### Why\n${artifacts.nextStepRecommendation.payload.reason}`,
                  artifacts.nextStepRecommendation.payload.exactOwner
                    ? `### Owner\n${artifacts.nextStepRecommendation.payload.exactOwner}`
                    : null,
                  artifacts.nextStepRecommendation.payload.dueDate
                    ? `### Due\n${artifacts.nextStepRecommendation.payload.dueDate}`
                    : null,
                  (artifacts.nextStepRecommendation.payload.blockingDependencies ?? []).length
                    ? `### Blocking Dependencies\n${(artifacts.nextStepRecommendation.payload.blockingDependencies ?? []).map((item) => `- ${item}`).join("\n")}`
                    : null,
                  artifacts.nextStepRecommendation.payload.backupPlan
                    ? `### Backup Plan\n${artifacts.nextStepRecommendation.payload.backupPlan}`
                    : null,
                ]
                  .filter((section): section is string => Boolean(section))
                  .join("\n\n")}
                className="markdown-block markdown-block--workflow markdown-block--meta"
              />
            ) : null}
          </div>

          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Track Recommendation</div>
            <SimpleMarkdown
              markdown={
                artifacts.trackRecommendation?.payload
                  ? [
                      `### Recommendation\n${artifacts.trackRecommendation.payload.recommendedTrack} (${artifacts.trackRecommendation.payload.confidence})`,
                      artifacts.trackRecommendation.payload.rationale
                        ? `### Rationale\n${artifacts.trackRecommendation.payload.rationale}`
                        : null,
                      (artifacts.trackRecommendation.payload.keyAssumptions ?? []).length
                        ? `### Key Assumptions\n${(artifacts.trackRecommendation.payload.keyAssumptions ?? []).map((item) => `- ${item}`).join("\n")}`
                        : null,
                      (
                        artifacts.trackRecommendation.payload
                          .questionsThatWouldChangeRecommendation ?? []
                      ).length
                        ? `### Questions That Would Change It\n${(artifacts.trackRecommendation.payload.questionsThatWouldChangeRecommendation ?? []).map((item) => `- ${item}`).join("\n")}`
                        : null,
                    ]
                      .filter((section): section is string => Boolean(section))
                      .join("\n\n")
                  : "No generated recommendation yet."
              }
              className="markdown-block markdown-block--workflow"
            />
          </div>
        </div>
      </section>

      <section hidden={activeTab !== "call_prep"} className="project-workflow-panel__tab-section">
        <ProjectCallPrepSection detail={detail} onRefresh={onRefresh} />
      </section>

      <section
        hidden={activeTab !== "relationship_history"}
        className="project-workflow-panel__tab-section"
      >
        <div className="project-workflow-panel__artifacts">
          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Relationship Snapshot</div>
            <ul className="call-prep-structured__list">
              {relationshipSnapshot.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Contact Channels</div>
            <div className="project-workflow-panel__link-list">
              {workflow.groupChatUrl ? (
                workflow.groupChatUrl.startsWith("http") ||
                workflow.groupChatUrl.startsWith("tg:") ? (
                  <a href={workflow.groupChatUrl} target="_blank" rel="noreferrer noopener">
                    Open group chat
                  </a>
                ) : (
                  <span className="call-prep-structured__value">{workflow.groupChatUrl}</span>
                )
              ) : (
                <span className="call-prep-structured__empty">No group chat attached.</span>
              )}
              {workflow.transcriptUrl ? (
                <a href={workflow.transcriptUrl} target="_blank" rel="noreferrer noopener">
                  Open transcript
                </a>
              ) : (
                <span className="call-prep-structured__empty">No transcript attached.</span>
              )}
            </div>
          </div>
          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Last Call Summary</div>
            <SimpleMarkdown
              markdown={workflow.callSummary ?? "No call summary captured yet."}
              className="markdown-block markdown-block--workflow"
            />
          </div>
          <div className="project-workflow-panel__artifact">
            <div className="project-workflow-panel__artifact-title">Internal Notes</div>
            <SimpleMarkdown
              markdown={workflow.internalNotes ?? "No internal notes captured yet."}
              className="markdown-block markdown-block--workflow"
            />
          </div>
          <div className="project-workflow-panel__artifact project-workflow-panel__artifact--communication">
            <div className="project-workflow-panel__artifact-title">Communication Intelligence</div>
            {agentContextQuery.isLoading ? (
              <div className="project-workflow-panel__artifact-meta">
                Loading recent email and calendar context...
              </div>
            ) : !communicationContext ? (
              <div className="call-prep-structured__empty">
                No communication intelligence is available for this project yet.
              </div>
            ) : (
              <div className="project-workflow-panel__communication-sections">
                <div className="project-workflow-panel__communication-section">
                  <div className="project-workflow-panel__communication-label">Signals</div>
                  {communicationContext.relevanceSignals.length === 0 ? (
                    <div className="call-prep-structured__empty">
                      No matched email or meeting signals yet.
                    </div>
                  ) : (
                    <div className="project-workflow-panel__communication-list">
                      {communicationContext.relevanceSignals.slice(0, 3).map((signal) => (
                        <div key={signal.id} className="project-workflow-panel__communication-item">
                          <div className="project-workflow-panel__communication-head">
                            <span>{signal.title}</span>
                            <span
                              className={`project-workflow-panel__signal-pill project-workflow-panel__signal-pill--${signal.severity}`}
                            >
                              {signal.severity}
                            </span>
                          </div>
                          <div className="project-workflow-panel__artifact-meta">
                            {signal.summary}
                          </div>
                          {signal.nextAction ? (
                            <div className="project-workflow-panel__communication-note">
                              Next step: {signal.nextAction}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="project-workflow-panel__communication-section">
                  <div className="project-workflow-panel__communication-label">Recent Email</div>
                  {communicationContext.recentEmails.length === 0 ? (
                    <div className="call-prep-structured__empty">No matched emails yet.</div>
                  ) : (
                    <div className="project-workflow-panel__communication-list">
                      {communicationContext.recentEmails.slice(0, 3).map((email) => (
                        <div
                          key={email.messageId}
                          className="project-workflow-panel__communication-item"
                        >
                          <div className="project-workflow-panel__communication-head">
                            <span>{email.subject ?? "No subject"}</span>
                            <span className="mono">{formatDateTime(email.receivedAt)}</span>
                          </div>
                          <div className="project-workflow-panel__artifact-meta">
                            {formatCommunicationCounterparty(email.fromName, email.fromEmail)}
                          </div>
                          {email.snippet ? (
                            <div className="project-workflow-panel__communication-note">
                              {email.snippet}
                            </div>
                          ) : null}
                          {email.recommendedAction ? (
                            <div className="project-workflow-panel__communication-note">
                              Reply angle: {email.recommendedAction}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="project-workflow-panel__communication-section">
                  <div className="project-workflow-panel__communication-label">
                    Upcoming Meetings
                  </div>
                  {communicationContext.upcomingMeetings.length === 0 ? (
                    <div className="call-prep-structured__empty">No matched meetings yet.</div>
                  ) : (
                    <div className="project-workflow-panel__communication-list">
                      {communicationContext.upcomingMeetings.slice(0, 2).map((meeting) => (
                        <div
                          key={meeting.eventId}
                          className="project-workflow-panel__communication-item"
                        >
                          <div className="project-workflow-panel__communication-head">
                            <span>{meeting.summary ?? "Untitled meeting"}</span>
                            <span className="mono">{formatDateTime(meeting.startAt)}</span>
                          </div>
                          <div className="project-workflow-panel__artifact-meta">
                            {meeting.organizerEmail ?? "Organizer unknown"}
                          </div>
                          {meeting.talkingPoints.length > 0 ? (
                            <ul className="call-prep-structured__list">
                              {meeting.talkingPoints.slice(0, 2).map((point) => (
                                <li key={point}>{point}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="project-workflow-panel__communication-section">
                  <div className="project-workflow-panel__communication-label">
                    Recommended Actions
                  </div>
                  {communicationContext.recommendedActions.length === 0 &&
                  communicationContext.talkingPoints.length === 0 ? (
                    <div className="call-prep-structured__empty">
                      No communication-specific actions captured yet.
                    </div>
                  ) : (
                    <>
                      {communicationContext.recommendedActions.length > 0 ? (
                        <ul className="call-prep-structured__list">
                          {communicationContext.recommendedActions.slice(0, 3).map((action) => (
                            <li key={`${action.label}-${action.dueAt ?? "none"}`}>
                              <strong>{action.label}</strong>: {action.reason}
                              {action.dueAt ? (
                                <span className="mono"> ({formatDateTime(action.dueAt)})</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {communicationContext.talkingPoints.length > 0 ? (
                        <>
                          <div className="project-workflow-panel__communication-label">
                            Talk tracks
                          </div>
                          <ul className="call-prep-structured__list">
                            {communicationContext.talkingPoints.slice(0, 3).map((point) => (
                              <li key={point}>{point}</li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="project-workflow-panel__timeline">
          <div className="project-workflow-panel__timeline-header">
            <span className="section-title">Relationship Timeline</span>
          </div>

          <div className="project-workflow-panel__composer">
            <textarea
              className="community-input"
              rows={3}
              value={eventNote}
              placeholder="Log a note, reminder, or commitment..."
              onChange={(e) => setEventNote(e.target.value)}
            />
            <button
              type="button"
              className="btn"
              disabled={!eventNote.trim() || createEventMutation.isPending}
              onClick={() => {
                createEventMutation.mutate(
                  {
                    projectId: detail.project.id,
                    event: {
                      eventType: "note_added",
                      note: eventNote.trim(),
                    },
                  },
                  {
                    onSuccess: () => {
                      setEventNote("");
                      void onRefresh();
                    },
                  }
                );
              }}
            >
              {createEventMutation.isPending ? "Saving..." : "Add Note"}
            </button>
          </div>

          <div className="project-workflow-panel__events">
            {visibleEvents.length === 0 ? (
              <div className="community-empty">No workflow events yet.</div>
            ) : (
              visibleEvents.map((event) => (
                <div key={event.id} className="project-workflow-panel__event">
                  <div className="project-workflow-panel__event-head">
                    <span>{formatWorkflowTimelineEventLabel(event.eventType)}</span>
                    <span className="mono">{formatDateTime(event.createdAt)}</span>
                  </div>
                  <div className="project-workflow-panel__event-meta">
                    {event.actorEmail ?? event.source}
                  </div>
                  {event.note && (
                    <SimpleMarkdown
                      markdown={event.note}
                      className="markdown-block markdown-block--workflow markdown-block--event"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
